/**
 * Lead lifecycle profiles.
 *
 * LCC_LEAD_STATUSES (vocabulary.ts) is the historical superset: 13 values that
 * grew by accretion and are still seeded into the Lead Command Center demo
 * project. It stays as-is so the demo's stored rows remain valid.
 *
 * CASCADE_LEAD_STATUSES is the canonical lifecycle for production LeadLoop
 * installs, and it is deliberately four values long. LeadLoop owns the period
 * between "someone contacted the business" and "this is a real, relevant,
 * sufficiently qualified opportunity". That period has a lifecycle of attention
 * and resolution, not a sales pipeline: a lead is untouched, being worked,
 * qualified, or abandoned. Estimating, scheduling, quoting, and closing belong
 * to the client's own downstream system, so LeadLoop must not name their stages.
 *
 * `verified_qualified` is where LeadLoop's responsibility ENDS. It deliberately
 * replaced the earlier `handed_off`, which described an event LeadLoop cannot
 * observe and does not require: there may be no downstream CRM, no salesperson,
 * and nobody to hand anything to. What LeadLoop can actually assert is that it
 * finished its own job — the intake was confirmed real, relevant, and qualified
 * enough to be treated as a legitimate opportunity. `handed_off` still reads
 * safely through CASCADE_STATUS_ALIASES so stored history keeps working, but it
 * is never offered as a choice again.
 *
 * What the retired statuses were reaching for now lives where it belongs:
 *   Needs Review, In Progress    -> `active`, plus the note
 *   Follow Up Later              -> `active`, plus next_follow_up_at
 *   Callback Needed, Waiting on Client, Appointment Set, Estimate Sent
 *                                -> `active`, plus Next Step / the note
 *   Qualified                    -> `active` + the Verified checkbox
 *   Converted / Signed! / Handed Off
 *                                -> `verified_qualified`
 *   Lost, Spam, Archived         -> `dismissed`
 *
 * Two states a reader sees are NOT stored here:
 *   - `Due` is derived from next_follow_up_at (see feed.ts). Storing it would
 *     mean a date and a status that can disagree about the same fact.
 *   - `Verified` is a reversible checkbox on the Leads table, and it is NOT the
 *     same claim as the status. Verified means "this looks like a real person
 *     or a real inquiry"; Verified & Qualified means "LeadLoop is finished and
 *     this is a legitimate opportunity". A lead can be the first without being
 *     the second, so a `verified` status would both duplicate and blur it.
 *
 * Legacy values map through CASCADE_STATUS_ALIASES on read. Aliases are
 * internal back-compat machinery only — they must never be offered to a user as
 * selectable choices, and nothing rewrites a stored value on their account (see
 * docs/cascade/data-homes.md).
 */

export const CASCADE_LEAD_STATUSES = [
  'new',
  'active',
  'verified_qualified',
  'dismissed',
] as const;

export type CascadeLeadStatus = (typeof CASCADE_LEAD_STATUSES)[number];

/** Display labels, in lifecycle order. Airtable stores these exact strings. */
export const CASCADE_LEAD_STATUS_LABELS: Record<CascadeLeadStatus, string> = {
  new: 'New',
  active: 'Active',
  verified_qualified: 'Verified & Qualified',
  dismissed: 'Dismissed',
};

export const CASCADE_LEAD_STATUS_LABEL_LIST = CASCADE_LEAD_STATUSES.map(
  (status) => CASCADE_LEAD_STATUS_LABELS[status],
);

/**
 * Every value we have ever written or read, mapped to its canonical
 * replacement. Keys are snake_case (post-lccStatusValue), so both "Follow Up
 * Later" and "follow_up_later" resolve through the same entry.
 *
 * Rationale for the non-obvious ones:
 *   spotted_opportunity     -> new         (noticed, not yet worked)
 *   qualified               -> active      (the Verified checkbox owns "real")
 *   ready_for_verification/verified
 *                           -> active      (same reason)
 *   follow_up_later         -> active      (a date is not a lifecycle stage)
 *   callback_needed, waiting_on_client, appointment_set, estimate_sent
 *                           -> active      (Next Step / Next Follow-Up own it)
 *   converted, signed       -> verified_qualified
 *                                         ("Won" is the client's word to say,
 *                                          after LeadLoop's job is done)
 *   handed_off              -> verified_qualified
 *                                         (the retired name for the same end
 *                                          state; see the header)
 *   spam, lost, archived    -> dismissed   (all three mean "stop working this")
 *
 * `verified_&_qualified` and `verified_and_qualified` are here because
 * toCascadeLeadStatus normalizes a display label by lowercasing and replacing
 * runs of whitespace with underscores — so the label a human types or an
 * Airtable single-select stores arrives in one of those two shapes, not as the
 * canonical value.
 */
export const CASCADE_STATUS_ALIASES: Record<string, CascadeLeadStatus> = {
  new_lead: 'new',
  spotted_opportunity: 'new',
  needs_review: 'active',
  in_progress: 'active',
  follow_up_later: 'active',
  qualified: 'active',
  ready_for_verification: 'active',
  verified: 'active',
  callback_needed: 'active',
  waiting_on_client: 'active',
  appointment_set: 'active',
  estimate_sent: 'active',
  recycle_later: 'active',
  converted: 'verified_qualified',
  'signed!': 'verified_qualified',
  signed: 'verified_qualified',
  handed_off: 'verified_qualified',
  'verified_&_qualified': 'verified_qualified',
  verified_and_qualified: 'verified_qualified',
  lost: 'dismissed',
  spam: 'dismissed',
  archived: 'dismissed',
};

export function isCascadeLeadStatus(value: string): value is CascadeLeadStatus {
  return (CASCADE_LEAD_STATUSES as readonly string[]).includes(value);
}

/**
 * Normalize any status — canonical, legacy, display-form, or unknown — to a
 * canonical value.
 *
 * Unknown input resolves to `active` rather than `new`: an unrecognized value
 * means somebody has been working this record in a way we do not model, and
 * calling that "New" would tell the reader nobody has touched it. Empty input
 * is a genuinely fresh record and stays `new`.
 */
export function toCascadeLeadStatus(value: string | null | undefined): CascadeLeadStatus {
  if (!value) return 'new';
  const key = value.trim().toLowerCase().replace(/\s+/g, '_');
  if (isCascadeLeadStatus(key)) return key;
  return CASCADE_STATUS_ALIASES[key] ?? 'active';
}

export function cascadeStatusLabel(status: string): string {
  return CASCADE_LEAD_STATUS_LABELS[toCascadeLeadStatus(status)];
}

/**
 * Semantic tone per status — the single place the lifecycle's colour language
 * is decided. Views map a tone to their own classes; they never branch on the
 * status value itself, so LeadLoop, the Leads list, card, table, detail, and
 * quick view cannot drift apart.
 *
 * Tones are named for what the state means, not for a colour, because the
 * palette differs per portal theme. `active` is deliberately included and
 * deliberately unused by the badge layer: Active is the ordinary state of a
 * working lead, and a badge that appears on almost every card says nothing.
 */
export type CascadeLeadStatusTone = 'new' | 'active' | 'verified_qualified' | 'dismissed';

export const CASCADE_LEAD_STATUS_TONES: Record<CascadeLeadStatus, CascadeLeadStatusTone> = {
  new: 'new',
  active: 'active',
  verified_qualified: 'verified_qualified',
  dismissed: 'dismissed',
};

export function cascadeStatusTone(status: string): CascadeLeadStatusTone {
  return CASCADE_LEAD_STATUS_TONES[toCascadeLeadStatus(status)];
}

/**
 * Terminal states: LeadLoop's job on this record is over, one way or the other.
 * They never resurface in the qualification queue on a timer, and a Next
 * Follow-Up left behind on a closed lead does not drag it back — reopening is an
 * explicit decision, not a date arriving.
 */
export const CASCADE_CLOSED_STATUSES = ['verified_qualified', 'dismissed'] as const;

export function isOpenCascadeStatus(status: string): boolean {
  return !(CASCADE_CLOSED_STATUSES as readonly string[]).includes(toCascadeLeadStatus(status));
}

/**
 * The one successful end state. LeadLoop finished its job on this record.
 *
 * Separate from `isOpenCascadeStatus` because "closed" lumps success in with
 * abandonment, and every surface that wants to render completion positively —
 * a green badge, a checkmark, a completed-count — needs to tell the two apart.
 */
export function isCascadeCompletedStatus(status: string): boolean {
  return toCascadeLeadStatus(status) === 'verified_qualified';
}

/**
 * Has anyone meaningfully processed this lead yet?
 *
 * `new` is the only state that means "nobody has". It is cleared by the first
 * Action Center submission that changes something (see interactions.ts) —
 * never by opening or reading the record, because reading is not deciding.
 */
export function isNewCascadeLead(status: string): boolean {
  return toCascadeLeadStatus(status) === 'new';
}

/**
 * The lifecycle transitions a user may ask for directly.
 *
 * Deliberately not "any status" — `new` is a state a lead leaves by being
 * worked, never one a user selects, and `active` is what working it produces.
 * Terminal decisions are the only ones worth a button.
 */
export const CASCADE_TERMINAL_TRANSITIONS = ['verified_qualified', 'dismissed'] as const;

export type CascadeTerminalTransition = (typeof CASCADE_TERMINAL_TRANSITIONS)[number];

export function isCascadeTerminalTransition(value: string): value is CascadeTerminalTransition {
  return (CASCADE_TERMINAL_TRANSITIONS as readonly string[]).includes(value);
}
